'use client'

import { cFormatter } from '@/lib/utils'
import { SkywardAccount } from '@/types/skyward-types'
import { CheckCircle, Circle } from 'lucide-react'
import React, { useEffect, useRef, useState } from 'react'

type Props = {
  label?: string
  accounts?: SkywardAccount[]
  value?: SkywardAccount
  type?: 'food' | 'fees'
  onChange?: (account: SkywardAccount) => void
}

export const SkywardCustomerSelector: React.FC<Props> = ({ label, value, onChange, accounts, type }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [total, setTotal] = useState(0.0)
  const [selectorText, setSelectorText] = useState('')
  const [selectorTextInfo, setSelectorTextInfo] = useState('')
  const wrapperRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    /**
     * Invoke Function onClick outside of element
     */
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as any)) {
        setIsOpen(false)
      }
    }
    // Bind
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      // dispose
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [wrapperRef])

  const handleSelected = (account: SkywardAccount) => {
    setIsOpen(false)
    if (account) {
      onChange && onChange(account)
    }
  }
  useEffect(() => {
    if (value == undefined) return
    setTotal(value?.existingFees?.reduce((total, fee) => (total += fee.due), 0.0) ?? 0.0)
    setSelectorText(`${value?.firstName} ${value?.lastName}`)
    if (type == 'food') {
      setSelectorTextInfo(
        `${value?.isStaff ? `Staff: (${value.nameId})` : `${value?.grade} (${value?.nameId})`.trim()}`.trim(),
      )
    } else {
      setSelectorTextInfo(
        `${value?.isStaff ? `Staff (${value?.nameId})` : `${value?.grade} (${value?.nameId})`.trim()}`,
      )
    }
  }, [value])
  if (!accounts) return null
  return (
    <div ref={wrapperRef}>
      {label && (
        <label htmlFor="combobox" className="block text-sm font-medium text-gray-700">
          {label}
        </label>
      )}
      <div className="relative mt-1">
        <div onClick={() => !isOpen && setIsOpen(true)} data-testid="account-selector">
          <div className="w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-12 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 sm:text-sm">
            {value ? (
              <div className="item-center flex text-lg">
                <span className="ml-3 truncate" data-testid="selected-account-name">
                  {selectorText}
                </span>
                <div className="flex flex-col justify-center text-xs">
                  <span className="ml-2 truncate text-gray-500" data-testid="selected-account-info">
                    {selectorTextInfo}
                  </span>
                </div>
              </div>
            ) : (
              <span className="ml-3 truncate text-lg">Select one</span>
            )}
          </div>

          <button
            type="button"
            className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none"
          >
            <svg
              className="h-5 w-5 text-gray-400"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M10 3a.75.75 0 01.55.24l3.25 3.5a.75.75 0 11-1.1 1.02L10 4.852 7.3 7.76a.75.75 0 01-1.1-1.02l3.25-3.5A.75.75 0 0110 3zm-3.76 9.2a.75.75 0 011.06.04l2.7 2.908 2.7-2.908a.75.75 0 111.1 1.02l-3.25 3.5a.75.75 0 01-1.1 0l-3.25-3.5a.75.75 0 01.04-1.06z"
                clipRule="evenodd"
              />
            </svg>
          </button>
          {isOpen && (
            <ul
              className="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
              id="options"
              role="listbox"
            >
              {accounts.map((c, i) => {
                let cFeeTotal = c?.existingFees?.reduce((total, fee) => (total += fee.due ?? 0) / 100.0, 0) ?? 0
                if (type == 'food') cFeeTotal = (c?.foodBalance ?? 0) / 100.0
                const infoText = `${cFeeTotal == 0 ? '' : cFormatter(cFeeTotal)} ${
                  c.isStaff ? 'Staff' : `${c.grade}`
                } (${c.nameId})`.trim()
                return (
                  <li
                    key={`account-${i}`}
                    className="relative cursor-default select-none py-2 pl-3 pr-9 text-gray-900"
                    id={`account-${i}`}
                    role="option"
                    tabIndex={-1}
                    onClick={() => handleSelected(c)}
                    data-testid={`account-option-${i}`}
                  >
                    <div className="flex items-center">
                      <span className="ml-3 truncate">{`${c.firstName} ${c.lastName}`}</span>
                      <span className="ml-2 truncate text-xs text-gray-400">{infoText}</span>
                    </div>
                    <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-indigo-600">
                      {value && (value == c ? <CheckCircle className="h-4 w-4" /> : <Circle className="h-4 w-4" />)}
                    </span>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  )
}
